/**
 * CoinBalanceBadge
 *
 * Compact pill showing the signed-in user's coin balance. Fetches the balance
 * on mount via coinService.getBalance(). Renders nothing while loading or when
 * the balance can't be resolved (e.g. signed-out / network error), so it is
 * safe to drop into a header without guarding.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getBalance } from '../../services/coinService';

export interface CoinBalanceBadgeProps {
  /** Wrap the badge in a Link to the coins page. Defaults to true. */
  asLink?: boolean;
  className?: string;
}

export default function CoinBalanceBadge({
  asLink = true,
  className = '',
}: CoinBalanceBadgeProps) {
  const { t } = useTranslation('coins');
  const [balance, setBalance] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    getBalance()
      .then((data) => {
        if (active) setBalance(data.balance);
      })
      .catch(() => {
        // Signed-out or transient error — keep the badge hidden.
        if (active) setBalance(null);
      });
    return () => {
      active = false;
    };
  }, []);

  if (balance === null) {
    return null;
  }

  const content = (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20 px-3 py-1 text-sm font-semibold text-[#003930] dark:text-[#B7DDD7] ${className}`}
    >
      <i className="ri-copper-coin-line text-base" aria-hidden="true"></i>
      {t('badgeLabel', { count: balance })}
    </span>
  );

  if (!asLink) {
    return content;
  }

  return (
    <Link to="/coins" className="inline-flex" title={t('pageTitle')}>
      {content}
    </Link>
  );
}
